import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { SideIllustration } from "../components/common/SideIllustration";
import { IMAGE_SOURCES } from "../mock/imageSources";
import { Mail, Send } from "lucide-react";

export default function ContactSupport() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      
      if (response.ok) {
        toast.success("Your enquiry has been submitted successfully. We will get back to you soon.");
        reset();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to submit enquiry. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to submit enquiry. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Contact &amp; Support</h1>
        <p className="max-w-3xl text-sm leading-relaxed text-slate-700 sm:text-base">
          For enquiries related to registration, syllabi or examination procedures, please use the contact form below. 
          You can also reach us directly at our email address.
        </p>
        <div className="flex items-center gap-2 text-sm">
          <Mail className="h-4 w-4 text-[#8c7648]" />
          <a href="mailto:info@globalmusic.org.uk" className="font-medium text-[#182033] hover:text-[#8c7648] transition-colors">
            info@globalmusic.org.uk
          </a>
        </div>
      </section>

      <section className="grid gap-6 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card className="border-slate-200 bg-white/80 shadow-sm backdrop-blur-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-900">
              Contact Form
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 text-sm">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="Your full name"
                    {...register("name", { required: "Please enter your name." })}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-600">{errors.name.message}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    {...register("email", { required: "Please provide a contact email." })}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder="Brief subject of your enquiry"
                  {...register("subject", { required: "Please indicate a subject." })}
                />
                {errors.subject && (
                  <p className="text-xs text-red-600">{errors.subject.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  rows={4}
                  placeholder="Please outline your enquiry in clear detail."
                  {...register("message", { required: "Please enter a message." })}
                />
                {errors.message && (
                  <p className="text-xs text-red-600">{errors.message.message}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="mt-2 w-full bg-[#182033] text-sm font-semibold text-white hover:bg-[#1e2a42]"
              >
                {isSubmitting ? (
                  "Submitting..."
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Enquiry
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <SideIllustration
            src={IMAGE_SOURCES.contactDesk}
            alt="Desk with sheet music used in examination correspondence"
          />
        </div>
      </section>
    </div>
  );
}
